// airbag-srs page — premium geometric aesthetic
'use client';

import React, { useState, useCallback } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Image from 'next/image';
import TrustpilotWidget from '@/components/TrustpilotWidget';

const criticalCards = [
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#CC1A1A" strokeWidth="1.5" strokeLinecap="round">
        <circle cx="12" cy="12" r="10"/>
        <path d="M12 8v4M12 16h.01"/>
      </svg>
    ),
    title: 'Deployed airbags cannot be reused',
    text: 'Airbags are single-use safety devices. Once deployed, they must be replaced with new units — no exceptions.',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#CC1A1A" strokeWidth="1.5" strokeLinecap="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/>
        <line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
    ),
    title: 'Warning lights mean compromised safety',
    text: 'An active SRS warning light means the system may not fire in another accident — your safety is at serious risk.',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#CC1A1A" strokeWidth="1.5" strokeLinecap="round">
        <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/>
      </svg>
    ),
    title: 'Incorrect repairs are dangerous',
    text: 'Improper coding or bypassed sensors can cause false deployment or complete non-deployment in a collision.',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#CC1A1A" strokeWidth="1.5" strokeLinecap="round">
        <path d="M9 12l2 2 4-4"/>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      </svg>
    ),
    title: 'Insurance-approved repairs require full SRS restoration',
    text: 'A properly documented SRS repair protects your insurance claim and ensures legal roadworthiness.',
  },
];

const serviceItems = [
  {
    title: 'Airbag module replacement',
    text: 'Driver, passenger, side curtain, and knee airbags — all replaced with new, approved units.',
  },
  {
    title: 'Seatbelt tensioner replacement',
    text: 'Pretensioners and retractors replaced to restore full restraint system function.',
  },
  {
    title: 'Crash sensor diagnostics and replacement',
    text: 'All impact sensors tested and replaced where necessary to ensure correct deployment logic.',
  },
  {
    title: 'SRS control module reset or replacement and coding',
    text: 'Module reset or full replacement with dealer-level coding to your specific vehicle.',
  },
  {
    title: 'Full system scan — zero fault codes before handover',
    text: 'Complete diagnostic verification confirms the system is fully operational before the vehicle leaves.',
  },
];

const codingBrands = [
  'Mercedes-Benz',
  'BMW',
  'Audi',
  'Volkswagen',
  'SEAT',
  'Škoda',
  'Porsche',
  'All VAG Group',
];

const signsItems = [
  "Airbag warning light illuminated on the dashboard",
  "Airbags have deployed following a collision",
  "Vehicle has been in any collision — even if bags didn't fire",
  "Purchasing a used vehicle where the SRS history is unknown",
];

const processSteps = [
  {
    num: '01',
    title: 'Full Diagnostic Scan',
    text: 'We connect dealer-level diagnostics to identify every fault code in the SRS system.',
  },
  {
    num: '02',
    title: 'Component Assessment',
    text: 'We identify all deployed, damaged, or faulty components — airbags, tensioners, sensors, and module.',
  },
  {
    num: '03',
    title: 'Parts Sourcing',
    text: 'We source genuine or manufacturer-approved replacement parts for your specific vehicle.',
  },
  {
    num: '04',
    title: 'Installation & Coding',
    text: 'All components are professionally installed and coded to your vehicle using manufacturer procedures.',
  },
  {
    num: '05',
    title: 'Final Verification',
    text: 'Full system rescan confirms zero fault codes and complete system readiness before handover.',
  },
];

const geoBg = {
  background: 'linear-gradient(135deg, #0a0a0a 0%, #121212 50%, #0d0d0d 100%)',
};

const geoTexture = {
  backgroundImage: `repeating-linear-gradient(
    -45deg,
    transparent,
    transparent 28px,
    rgba(255,255,255,0.018) 28px,
    rgba(255,255,255,0.018) 29px
  )`,
};

const altBg = {
  background: 'linear-gradient(135deg, #0d0d0d 0%, #111111 100%)',
};

function GeoDivider() {
  return (
    <div className="relative h-px overflow-visible" aria-hidden="true">
      <div
        className="absolute inset-0"
        style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(204,26,26,0.3) 30%, rgba(204,26,26,0.3) 70%, transparent 100%)' }}
      />
      <div
        className="absolute left-1/2 -translate-x-1/2 -top-2 w-4 h-4"
        style={{ background: '#CC1A1A', clipPath: 'polygon(50% 0%, 100% 100%, 0% 100%)', opacity: 0.5 }}
      />
    </div>
  );
}

export default function AirbagSrsPage() {
  // ── Booking form state ──────────────────────────────────────────────────────
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    registration: '',
    vehicleMake: '',
    issue: '',
    otherIssue: '',
    website: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [showModal, setShowModal] = useState(false);
  const [bookingReference, setBookingReference] = useState('');
  const [rateLimitMsg, setRateLimitMsg] = useState('');
  const lastSubmitRef = React.useRef<number>(0);

  const validateField = (name: string, value: string): string => {
    if (name === 'name') {
      if (!value.trim()) return 'Full name is required';
      if (value.trim().length < 2) return 'Name must be at least 2 characters';
    }
    if (name === 'phone') {
      if (!value.trim()) return 'Phone number is required';
      const stripped = value.replace(/[\s\-().]/g, '');
      if (!/^(\+44|0044|0)[1-9]\d{8,9}$/.test(stripped)) return 'Enter a valid UK phone number';
    }
    if (name === 'registration') {
      if (!value.trim()) return 'Vehicle registration is required';
    }
    if (name === 'vehicleMake') {
      if (!value.trim()) return 'Vehicle make & model is required';
    }
    if (name === 'issue') {
      if (!value) return 'Please select a warning light or issue';
    }
    if (name === 'email' && value.trim()) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) return 'Enter a valid email address';
    }
    return '';
  };

  const handleFieldChange = (name: string, value: string) => {
    setFormData(p => ({ ...p, [name]: name === 'registration' ? value.toUpperCase() : value }));
    if (touched[name]) {
      const err = validateField(name, value);
      setFieldErrors(prev => { const next = { ...prev }; if (err) next[name] = err; else delete next[name]; return next; });
    }
  };

  const handleFieldBlur = (name: string, value: string) => {
    setTouched(prev => ({ ...prev, [name]: true }));
    const err = validateField(name, value);
    setFieldErrors(prev => { const next = { ...prev }; if (err) next[name] = err; else delete next[name]; return next; });
  };

  const inputClass = (name: string) => {
    const base = 'w-full px-4 py-3 bg-[#1a1a1a] border text-white placeholder-white/30 font-dm-sans text-sm focus:outline-none transition-colors rounded-sm';
    if (touched[name] && fieldErrors[name]) return `${base} border-red-500 focus:border-red-500`;
    if (touched[name] && !fieldErrors[name]) return `${base} border-green-600/60 focus:border-green-600/60`;
    return `${base} border-white/10 focus:border-[#CC1A1A]/60`;
  };

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    // ── Rate limiting ────────────────────────────────────────────────────────
    const now = Date.now();
    if (now - lastSubmitRef.current < 60000) {
      const remaining = Math.ceil((60000 - (now - lastSubmitRef.current)) / 1000);
      setRateLimitMsg(`Please wait ${remaining} seconds before submitting again.`);
      return;
    }
    setRateLimitMsg('');

    // ── Honeypot check ───────────────────────────────────────────────────────
    if (formData.website) {
      setShowModal(true);
      return;
    }

    // Validate all required fields
    const requiredFields = ['name', 'phone', 'registration', 'vehicleMake', 'issue'];
    const allTouched: Record<string, boolean> = {};
    const errors: Record<string, string> = {};
    requiredFields.forEach(f => {
      allTouched[f] = true;
      const err = validateField(f, formData[f as keyof typeof formData] as string);
      if (err) errors[f] = err;
    });
    if (formData.issue === 'Other' && !formData.otherIssue.trim()) {
      errors.otherIssue = 'Please describe the issue';
      allTouched.otherIssue = true;
    }
    if (formData.email.trim()) {
      const emailErr = validateField('email', formData.email);
      if (emailErr) { errors.email = emailErr; allTouched.email = true; }
    }
    setTouched(prev => ({ ...prev, ...allTouched }));
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setSubmitting(true);
    setFormError('');
    lastSubmitRef.current = Date.now();

    const SUPABASE_URL = process.env.NEXT_PUBLIC_SUPABASE_URL;
    const SUPABASE_ANON_KEY = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

    // ── Generate booking reference ────────────────────────────────────────────
    const year = new Date().getFullYear();
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    const rand = Array.from({ length: 6 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
    const bookingRef = `NLB-${year}-${rand}`;

    // ── Save to Supabase (independent of email) ──────────────────────────────
    try {
      const { createClient } = await import('@/lib/supabase/client');
      const supabase = createClient();
      await supabase.from('bookings').insert({
        service_type: 'airbag-srs',
        name: formData.name,
        phone: formData.phone,
        email: formData.email || null,
        vehicle_reg: formData.registration.toUpperCase(),
        vehicle_make_model: formData.vehicleMake,
        message: (formData.issue === 'Other' ? formData.otherIssue : formData.issue) || null,
        photo_urls: null,
        status: 'new',
        reference: bookingRef,
      });
    } catch {
      // Supabase save failure must not block email
    }

    // ── Send email (independent of Supabase) ─────────────────────────────────
    try {
      const body = new FormData();
      body.append('name', formData.name);
      body.append('phone', formData.phone);
      body.append('email', formData.email);
      body.append('registration', formData.registration.toUpperCase());
      body.append('vehicleMake', formData.vehicleMake);
      body.append('issue', formData.issue === 'Other' ? formData.otherIssue : formData.issue);
      body.append('service', 'Airbag & SRS Repair');
      body.append('subject', `New Airbag/SRS Booking – ${formData.name} – ${formData.registration.toUpperCase()}`);
      body.append('successMessage', "Thank you! We'll call you within 1 hour to schedule your diagnostic appointment.");
      body.append('bookingRef', bookingRef);

      const res = await fetch(`${SUPABASE_URL}/functions/v1/send-booking-email`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${SUPABASE_ANON_KEY}` },
        body,
      });
      if (!res.ok) throw new Error('Email failed');
    } catch {
      // Email failure must not block success if Supabase saved
    }

    setFormData({ name: '', phone: '', email: '', registration: '', vehicleMake: '', issue: '', otherIssue: '', website: '' });
    setFieldErrors({});
    setTouched({});
    setSubmitting(false);
    setBookingReference(bookingRef);
    setShowModal(true);
  }, [formData]);

  const handleCloseModal = () => {
    setShowModal(false);
    if (typeof window !== 'undefined') {
      window.location.href = `/booking-confirmed?service=airbag-srs&ref=${encodeURIComponent(bookingReference)}`;
    }
  };

  return (
    <>
      {/* ─── SUCCESS MODAL ─── */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.75)' }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-heading-ab"
        >
          <div
            className="relative w-full max-w-md rounded-sm p-8 flex flex-col items-center text-center"
            style={{ background: '#121212', border: '1px solid rgba(255,255,255,0.08)' }}
          >
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
              style={{ background: 'rgba(22,163,74,0.15)', border: '2px solid rgba(22,163,74,0.5)' }}
              aria-hidden="true"
            >
              <svg
                width="40" height="40" viewBox="0 0 24 24" fill="none"
                stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                style={{ animation: 'checkPop 0.4s ease-out' }}
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h2 id="modal-heading-ab" className="font-dm-sans font-black text-white text-2xl mb-3">
              Booking Received!
            </h2>
            <p className="font-dm-sans text-white/60 text-[15px] leading-relaxed mb-4">
              Thank you! We&apos;ll call you within 1 hour to schedule your diagnostic appointment.
            </p>
            {bookingReference && (
              <div
                className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-sm"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
              >
                <span className="font-dm-sans text-[11px] uppercase tracking-[0.2em] text-white/35">Reference</span>
                <span className="font-dm-sans text-[13px] font-bold text-white/80 tracking-wider">{bookingReference}</span>
              </div>
            )}
            <button
              onClick={handleCloseModal}
              className="w-full py-3 font-dm-sans font-bold text-[13px] uppercase tracking-[0.15em] text-white transition-all duration-200 rounded-sm"
              style={{ background: '#CC1A1A' }}
              onMouseEnter={e => (e.currentTarget.style.background = '#E02020')}
              onMouseLeave={e => (e.currentTarget.style.background = '#CC1A1A')}
            >
              Close
            </button>
          </div>
        </div>
      )}
      <style>{`@keyframes checkPop { 0% { transform: scale(0.5); opacity: 0; } 70% { transform: scale(1.15); } 100% { transform: scale(1); opacity: 1; } }`}</style>
      <Header />
      <main className="min-h-screen" style={geoBg}>
        {/* Global geometric texture overlay */}
        <div className="fixed inset-0 pointer-events-none z-0" style={geoTexture} aria-hidden="true" />

        {/* HERO SECTION */}
        <section className="relative pt-32 pb-20 px-6 overflow-hidden z-10">
          <div
            className="absolute inset-0 pointer-events-none"
            aria-hidden="true"
            style={{ background: 'radial-gradient(ellipse 60% 80% at 80% 50%, rgba(204,26,26,0.07) 0%, transparent 70%)' }}
          />

          <div className="relative max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              {/* Left: Text — with red left-border accent */}
              <div className="pl-6" style={{ borderLeft: '3px solid #CC1A1A' }}>
                <div className="inline-flex items-center gap-2 px-4 py-2 border border-[#CC1A1A]/40 bg-[#CC1A1A]/10 mb-8">
                  <span className="w-2 h-2 rounded-full bg-[#CC1A1A] animate-pulse" />
                  <span className="font-dm-sans text-xs font-bold tracking-[0.25em] uppercase text-[#CC1A1A]">
                    SRS Specialist
                  </span>
                </div>

                <h1 className="font-fraunces font-bold text-white text-4xl md:text-5xl lg:text-6xl leading-[1.1] mb-3 tracking-tight">
                  Airbags Deployed? We Restore Your{' '}
                  <em className="text-[#CC1A1A] not-italic">Safety Systems</em>{' '}to Factory Condition
                </h1>
                <div className="w-16 h-[3px] bg-[#CC1A1A] mb-6" />

                <p className="font-dm-sans text-[#C0C0C0] text-lg leading-relaxed mb-10 max-w-xl">
                  Full SRS diagnostics, module replacement, and coding — no warning lights, no shortcuts.
                </p>

                <div className="flex flex-wrap gap-4 mb-10">
                  <a
                    href="#booking-form"
                    onClick={e => { e.preventDefault(); document.getElementById('booking-form')?.scrollIntoView({ behavior: 'smooth' }); }}
                    className="inline-flex items-center gap-2 px-8 py-4 text-white font-dm-sans font-bold text-sm uppercase tracking-widest transition-all duration-200"
                    style={{ background: '#CC1A1A', borderRadius: '3px' }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#E02020')}
                    onMouseLeave={e => (e.currentTarget.style.background = '#CC1A1A')}
                  >
                    Book Diagnostic
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  </a>
                  <a
                    href="tel:01614001687"
                    className="inline-flex items-center gap-2 px-8 py-4 text-white font-dm-sans font-bold text-sm uppercase tracking-widest transition-all duration-200"
                    style={{ background: '#1a1a1a', border: '1px solid #CC1A1A', borderRadius: '3px' }}
                    onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(204,26,26,0.7)')}
                    onMouseLeave={e => (e.currentTarget.style.borderColor = '#CC1A1A')}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.07 1.18 2 2 0 012.03 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
                    </svg>
                    01614001687
                  </a>
                </div>
              </div>

              {/* Right: Image — keep existing illustration */}
              <div className="relative">
                <div className="aspect-[4/3] bg-[#111111] border border-white/[0.08] overflow-hidden" style={{ aspectRatio: '4/3' }}>
                  <Image
                    src="/assets/images/airbag_srs_hero.png"
                    alt="Exploded isometric view of car airbag and SRS safety system components"
                    width={800}
                    height={600}
                    className="w-full h-full object-cover"
                    priority
                  />
                </div>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#CC1A1A]" />
              </div>
            </div>
          </div>
        </section>

        <GeoDivider />

        {/* SECTION 1 — What is SRS? */}
        <section className="py-20 px-6 relative z-10" style={altBg}>
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <p className="font-dm-sans text-xs font-bold tracking-[0.25em] uppercase text-[#CC1A1A] mb-4">Safety System Explained</p>
                <h2 className="font-fraunces font-bold text-white text-3xl md:text-4xl leading-tight mb-2">
                  What is the SRS system?
                </h2>
                <div className="w-12 h-[3px] bg-[#CC1A1A] mb-6" />
                <p className="font-dm-sans text-[#C0C0C0] text-base leading-relaxed">
                  The Supplementary Restraint System (SRS) is the complete network of safety components in your vehicle — including airbags, seatbelt tensioners, crash sensors, and the SRS control module. After a collision where airbags deploy, every component in the system requires professional assessment and replacement. A warning light on the dashboard means the system may not activate in a future accident — that&apos;s a serious safety risk.
                </p>
              </div>

              <div className="bg-[#111111] border border-[#CC1A1A]/30 p-8">
                <p className="font-dm-sans text-xs font-bold tracking-[0.2em] uppercase text-[#CC1A1A] mb-5">SRS System Components</p>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: 'Airbags', sub: 'Driver, passenger, side, curtain, knee' },
                    { label: 'Seatbelt Tensioners', sub: 'Pretensioners & retractors' },
                    { label: 'Crash Sensors', sub: 'Impact detection network' },
                    { label: 'SRS Control Module', sub: 'Central processing unit' },
                  ]?.map((item) => (
                    <div key={item?.label} className="bg-[#0a0a0a] border border-white/[0.08] p-4 hover:border-[#CC1A1A]/30 transition-colors">
                      <div className="w-2 h-2 rounded-full bg-[#CC1A1A] mb-3" />
                      <p className="font-dm-sans text-white text-sm font-semibold mb-1">{item?.label}</p>
                      <p className="font-dm-sans text-[#C0C0C0] text-xs leading-relaxed">{item?.sub}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-5 pt-5 border-t border-white/[0.06]">
                  <p className="font-dm-sans text-[#C0C0C0] text-xs leading-relaxed">
                    All four components must be assessed and replaced after airbag deployment.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <GeoDivider />

        {/* SECTION 2 — Why proper repair is critical */}
        <section className="py-20 px-6 relative z-10" style={geoBg}>
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <p className="font-dm-sans text-xs font-bold tracking-[0.25em] uppercase text-[#CC1A1A] mb-4">Non-Negotiable</p>
              <h2 className="font-fraunces font-bold text-white text-3xl md:text-4xl leading-tight mb-2">
                Why proper SRS repair is critical
              </h2>
              <div className="w-12 h-[3px] bg-[#CC1A1A] mx-auto" />
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {criticalCards?.map((card) => (
                <div key={card?.title} className="bg-[#111111] border border-white/[0.08] p-7 hover:border-[#CC1A1A]/30 transition-colors duration-300">
                  <div className="w-12 h-12 bg-[#CC1A1A]/10 flex items-center justify-center mb-5">
                    {card?.icon}
                  </div>
                  <h3 className="font-fraunces font-semibold text-white text-lg mb-3 leading-snug">{card?.title}</h3>
                  <p className="font-dm-sans text-[#C0C0C0] text-sm leading-relaxed">{card?.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <GeoDivider />

        {/* SECTION 3 — Our specialist service */}
        <section className="py-20 px-6 relative z-10" style={altBg}>
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16 items-start">
              <div>
                <p className="font-dm-sans text-xs font-bold tracking-[0.25em] uppercase text-[#CC1A1A] mb-4">Full Service</p>
                <h2 className="font-fraunces font-bold text-white text-3xl md:text-4xl leading-tight mb-2">
                  Our specialist service includes
                </h2>
                <div className="w-12 h-[3px] bg-[#CC1A1A] mb-6" />
                <p className="font-dm-sans text-[#C0C0C0] text-base leading-relaxed">
                  Every aspect of your SRS system is addressed — from individual components to full system verification.
                </p>
              </div>

              <div className="space-y-4">
                {serviceItems?.map((item, i) => (
                  <div key={i} className="flex items-start gap-4 bg-[#111111] border border-white/[0.08] px-6 py-5 hover:border-[#CC1A1A]/20 transition-colors">
                    <div className="shrink-0 mt-0.5">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#CC1A1A" strokeWidth="2.5" strokeLinecap="round">
                        <path d="M20 6L9 17l-5-5"/>
                      </svg>
                    </div>
                    <div>
                      <p className="font-dm-sans text-white text-sm font-semibold mb-1">{item?.title}</p>
                      <p className="font-dm-sans text-[#C0C0C0] text-sm leading-relaxed">{item?.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <GeoDivider />

        {/* SECTION 4 — The coding aspect */}
        <section className="py-20 px-6 relative z-10" style={geoBg}>
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="bg-[#111111] border border-[#CC1A1A]/30 p-8">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#CC1A1A]/10 border border-[#CC1A1A]/30 mb-6">
                  <span className="font-dm-sans text-xs font-bold tracking-[0.2em] uppercase text-[#CC1A1A]">Dealer-Level Programming</span>
                </div>
                <p className="font-dm-sans text-[#E0E0E0] text-xs font-bold tracking-[0.2em] uppercase mb-4">Vehicles We Code</p>
                <div className="grid grid-cols-2 gap-3">
                  {codingBrands?.map((brand) => (
                    <div
                      key={brand}
                      className={`px-4 py-3 border flex items-center gap-2 ${
                        brand === 'All VAG Group' ? 'border-[#CC1A1A]/40 bg-[#CC1A1A]/5' : 'border-white/[0.08] bg-[#0a0a0a]'
                      }`}
                    >
                      <div className="w-1.5 h-1.5 rounded-full bg-[#CC1A1A] shrink-0" />
                      <span className={`font-dm-sans text-sm font-semibold ${
                        brand === 'All VAG Group' ? 'text-[#CC1A1A]' : 'text-[#E0E0E0]'
                      }`}>{brand}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <p className="font-dm-sans text-xs font-bold tracking-[0.25em] uppercase text-[#CC1A1A] mb-4">Critical Step</p>
                <h2 className="font-fraunces font-bold text-white text-3xl md:text-4xl leading-tight mb-2">
                  Why module coding matters
                </h2>
                <div className="w-12 h-[3px] bg-[#CC1A1A] mb-6" />
                <p className="font-dm-sans text-[#C0C0C0] text-base leading-relaxed">
                  On modern Mercedes-Benz, BMW, Audi, and VAG Group vehicles, replacement SRS modules must be coded to the specific vehicle using dealer-level diagnostic tools. Without proper coding, the module won&apos;t communicate correctly with the car&apos;s other systems — meaning airbags may not deploy when they should, or the warning light will remain active. We use manufacturer-grade programming tools to ensure every replacement module is fully paired and verified.
                </p>
              </div>
            </div>
          </div>
        </section>

        <GeoDivider />

        {/* SECTION 5 — Signs you need SRS repair */}
        <section className="py-20 px-6 relative z-10" style={altBg}>
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16 items-start">
              <div>
                <p className="font-dm-sans text-xs font-bold tracking-[0.25em] uppercase text-[#CC1A1A] mb-4">Warning Signs</p>
                <h2 className="font-fraunces font-bold text-white text-3xl md:text-4xl leading-tight mb-2">
                  Signs you need SRS repair
                </h2>
                <div className="w-12 h-[3px] bg-[#CC1A1A] mb-6" />
                <p className="font-dm-sans text-[#C0C0C0] text-base leading-relaxed mb-8">
                  If any of the following apply, your SRS system requires immediate professional attention.
                </p>

                <div className="space-y-4">
                  {signsItems?.map((item, i) => (
                    <div key={i} className="flex items-start gap-4 bg-[#111111] border border-white/[0.08] px-6 py-5 hover:border-amber-500/20 transition-colors">
                      <div className="shrink-0 mt-0.5">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2.5" strokeLinecap="round">
                          <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                          <line x1="12" y1="9" x2="12" y2="13"/>
                          <line x1="12" y1="17" x2="12.01" y2="17"/>
                        </svg>
                      </div>
                      <p className="font-dm-sans text-[#C0C0C0] text-sm leading-relaxed">{item}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-[#111111] border-l-4 border-[#CC1A1A] p-8">
                <div className="flex items-start gap-4">
                  <div className="shrink-0 w-10 h-10 bg-[#CC1A1A]/10 flex items-center justify-center">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#CC1A1A" strokeWidth="2" strokeLinecap="round">
                      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                      <line x1="12" y1="9" x2="12" y2="13"/>
                      <line x1="12" y1="17" x2="12.01" y2="17"/>
                    </svg>
                  </div>
                  <div>
                    <p className="font-dm-sans text-xs font-bold tracking-[0.2em] uppercase text-[#CC1A1A] mb-3">Important Warning</p>
                    <p className="font-dm-sans text-[#C0C0C0] text-sm leading-relaxed">
                      Avoid any vehicle where the previous owner has disabled or bypassed the SRS system — this is illegal and extremely dangerous.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <GeoDivider />

        {/* SECTION 6 — Our process */}
        <section className="py-20 px-6 relative z-10" style={geoBg}>
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <p className="font-dm-sans text-xs font-bold tracking-[0.25em] uppercase text-[#CC1A1A] mb-4">Step by Step</p>
              <h2 className="font-fraunces font-bold text-white text-3xl md:text-4xl leading-tight mb-2">
                Our process
              </h2>
              <div className="w-12 h-[3px] bg-[#CC1A1A] mx-auto" />
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
              {processSteps?.map((step) => (
                <div key={step?.num} className="bg-[#111111] border border-white/[0.08] p-7 relative hover:border-[#CC1A1A]/30 transition-colors duration-300">
                  <div className="font-fraunces text-5xl font-light text-[#CC1A1A] mb-5 leading-none">{step?.num}</div>
                  <h3 className="font-fraunces font-semibold text-white text-lg mb-3 leading-snug">{step?.title}</h3>
                  <p className="font-dm-sans text-[#C0C0C0] text-sm leading-relaxed">{step?.text}</p>
                  <div className="absolute bottom-0 left-0 w-full h-[2px] bg-gradient-to-r from-[#CC1A1A] to-transparent" />
                </div>
              ))}
            </div>
          </div>
        </section>

        <GeoDivider />

        {/* SECTION 7 — Before/After */}
        <section className="py-20 px-6 relative z-10" style={altBg}>
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <p className="font-dm-sans text-xs font-bold tracking-[0.25em] uppercase text-[#CC1A1A] mb-4">Proof of Quality</p>
              <h2 className="font-fraunces font-bold text-white text-3xl md:text-4xl leading-tight mb-2">
                SRS restoration, done correctly
              </h2>
              <div className="w-12 h-[3px] bg-[#CC1A1A] mx-auto" />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="relative">
                <div className="aspect-[4/3] bg-[#111111] border border-white/[0.08] flex flex-col items-center justify-center gap-4">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1" strokeLinecap="round" opacity="0.2">
                    <rect x="3" y="3" width="18" height="18" rx="2"/>
                    <circle cx="8.5" cy="8.5" r="1.5"/>
                    <path d="M21 15l-5-5L5 21"/>
                  </svg>
                  <p className="font-dm-sans text-white/20 text-xs tracking-wider uppercase">Deployed / Damaged Airbag Photo</p>
                </div>
                <div className="absolute top-4 left-4 px-3 py-1 bg-[#CC1A1A] font-dm-sans text-white text-xs font-bold uppercase tracking-widest">
                  Before
                </div>
              </div>

              <div className="relative">
                <div className="aspect-[4/3] bg-[#111111] border border-white/[0.08] flex flex-col items-center justify-center gap-4">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1" strokeLinecap="round" opacity="0.2">
                    <rect x="3" y="3" width="18" height="18" rx="2"/>
                    <circle cx="8.5" cy="8.5" r="1.5"/>
                    <path d="M21 15l-5-5L5 21"/>
                  </svg>
                  <p className="font-dm-sans text-white/20 text-xs tracking-wider uppercase">Clean, Restored Interior Photo</p>
                </div>
                <div className="absolute top-4 left-4 px-3 py-1 bg-[#161616] border border-white/20 font-dm-sans text-white text-xs font-bold uppercase tracking-widest">
                  After
                </div>
              </div>
            </div>

            <p className="font-dm-sans text-white/30 text-sm text-center mt-6">
              Every system is verified fault-free before the vehicle leaves our workshop.
            </p>
          </div>
        </section>

        <GeoDivider />

        {/* CTA SECTION */}
        <section id="contact" className="py-24 px-6 relative z-10" style={geoBg}>
          <div className="max-w-4xl mx-auto text-center">
            <p className="font-dm-sans text-xs font-bold tracking-[0.25em] uppercase text-[#CC1A1A] mb-6">Free Diagnostic</p>
            <h2 className="font-fraunces font-bold text-white text-3xl md:text-5xl leading-tight mb-2">
              Warning light on? Book a free SRS diagnostic.
            </h2>
            <div className="w-16 h-[3px] bg-[#CC1A1A] mx-auto mb-6" />
            <p className="font-dm-sans text-[#C0C0C0] text-lg leading-relaxed mb-10 max-w-2xl mx-auto">
              We&apos;ll scan your system, identify every fault, and tell you exactly what&apos;s needed — at no cost.
            </p>

            <div className="flex flex-wrap gap-4 justify-center mb-10">
              <a
                href="#booking-form"
                onClick={e => { e.preventDefault(); document.getElementById('booking-form')?.scrollIntoView({ behavior: 'smooth' }); }}
                className="inline-flex items-center gap-2 px-10 py-4 text-white font-dm-sans font-bold text-sm uppercase tracking-widest transition-all duration-200"
                style={{ background: '#CC1A1A', borderRadius: '3px' }}
                onMouseEnter={e => (e.currentTarget.style.background = '#E02020')}
                onMouseLeave={e => (e.currentTarget.style.background = '#CC1A1A')}
              >
                Book Diagnostic
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </a>
              <a
                href="tel:01614001687"
                className="inline-flex items-center gap-2 px-10 py-4 text-white font-dm-sans font-bold text-sm uppercase tracking-widest transition-all duration-200"
                style={{ background: '#1a1a1a', border: '1px solid #CC1A1A', borderRadius: '3px' }}
                onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(204,26,26,0.7)')}
                onMouseLeave={e => (e.currentTarget.style.borderColor = '#CC1A1A')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.07 1.18 2 2 0 012.03 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
                </svg>
                01614001687
              </a>
            </div>

            <a
              href="mailto:contactus@nolimitauto.co.uk"
              className="font-dm-sans text-white/40 text-sm hover:text-[#CC1A1A] transition-colors"
            >
              contactus@nolimitauto.co.uk
            </a>
          </div>
        </section>

        <GeoDivider />

        {/* ── BOOKING FORM SECTION ── */}
        {/* Trustpilot Mini Widget */}
        <div style={{ background: '#121212', padding: '48px 24px 0' }}>
          <div className="max-w-2xl mx-auto">
            <TrustpilotWidget templateId="53aa8807dec7e10d38f59f32" height="350px" />
          </div>
        </div>
        <section id="booking-form" className="py-20 px-6 relative z-10" style={{ background: '#121212' }}>
          <div className="max-w-2xl mx-auto">
            <div className="mb-10">
              <p className="font-dm-sans text-xs font-bold tracking-[0.25em] uppercase text-[#CC1A1A] mb-4">Book Online</p>
              <h2 className="font-fraunces font-bold text-white text-3xl md:text-4xl leading-tight mb-2">
                Book Your Diagnostic
              </h2>
              <div className="w-12 h-[3px] bg-[#CC1A1A] mb-4" />
              <p className="font-dm-sans text-[#C0C0C0] text-base">
                Fill in the form below and we&apos;ll call you within 1 hour to schedule your diagnostic appointment.
              </p>
            </div>

            {submitted ? (
              <div className="bg-[#1a1a1a] border border-[#CC1A1A]/40 rounded p-8 text-center">
                <div className="w-14 h-14 bg-[#CC1A1A]/10 border border-[#CC1A1A]/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#CC1A1A" strokeWidth="2" strokeLinecap="round">
                    <path d="M20 6L9 17l-5-5"/>
                  </svg>
                </div>
                <p className="font-dm-sans font-bold text-white text-lg mb-2">Booking Received!</p>
                <p className="font-dm-sans text-[#C0C0C0] text-base">
                  Thank you! We&apos;ll call you within 1 hour to schedule your diagnostic appointment.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                {/* Honeypot — hidden from real users, visible to bots */}
                <div
                  style={{ position: 'absolute', left: '-9999px', top: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}
                  aria-hidden="true"
                >
                  <label htmlFor="website-hp-ab">Website</label>
                  <input
                    id="website-hp-ab"
                    name="website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formData.website}
                    onChange={e => setFormData(p => ({ ...p, website: e.target.value }))}
                  />
                </div>
                {/* Name */}
                <div>
                  <label className="block font-dm-sans text-sm font-semibold text-white mb-2">
                    Full Name <span className="text-[#CC1A1A]">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={e => handleFieldChange('name', e.target.value)}
                    onBlur={e => handleFieldBlur('name', e.target.value)}
                    placeholder="Your full name"
                    className={inputClass('name')}
                  />
                  {touched.name && fieldErrors.name && (
                    <p className="mt-1.5 font-dm-sans text-[12px] text-red-400">{fieldErrors.name}</p>
                  )}
                </div>
                {/* Phone */}
                <div>
                  <label className="block font-dm-sans text-sm font-semibold text-white mb-2">
                    Phone Number <span className="text-[#CC1A1A]">*</span>
                  </label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={e => handleFieldChange('phone', e.target.value)}
                    onBlur={e => handleFieldBlur('phone', e.target.value)}
                    placeholder="e.g. 07700 900000"
                    className={inputClass('phone')}
                  />
                  {touched.phone && fieldErrors.phone && (
                    <p className="mt-1.5 font-dm-sans text-[12px] text-red-400">{fieldErrors.phone}</p>
                  )}
                </div>
                {/* Email */}
                <div>
                  <label className="block font-dm-sans text-sm font-semibold text-white mb-2">
                    Email Address <span className="text-white/30 font-normal">(optional — for confirmation)</span>
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={e => handleFieldChange('email', e.target.value)}
                    onBlur={e => handleFieldBlur('email', e.target.value)}
                    placeholder="your@email.com"
                    className={inputClass('email')}
                  />
                  {touched.email && fieldErrors.email && (
                    <p className="mt-1.5 font-dm-sans text-[12px] text-red-400">{fieldErrors.email}</p>
                  )}
                </div>
                {/* Registration */}
                <div>
                  <label className="block font-dm-sans text-sm font-semibold text-white mb-2">
                    Vehicle Registration <span className="text-[#CC1A1A]">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.registration}
                    onChange={e => handleFieldChange('registration', e.target.value)}
                    onBlur={e => handleFieldBlur('registration', e.target.value)}
                    placeholder="e.g. AB12 CDE"
                    className={`${inputClass('registration')} uppercase`}
                  />
                  {touched.registration && fieldErrors.registration && (
                    <p className="mt-1.5 font-dm-sans text-[12px] text-red-400">{fieldErrors.registration}</p>
                  )}
                </div>
                {/* Vehicle Make/Model */}
                <div>
                  <label className="block font-dm-sans text-sm font-semibold text-white mb-2">
                    Vehicle Make &amp; Model <span className="text-[#CC1A1A]">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.vehicleMake}
                    onChange={e => handleFieldChange('vehicleMake', e.target.value)}
                    onBlur={e => handleFieldBlur('vehicleMake', e.target.value)}
                    placeholder="e.g. BMW 3 Series 2019"
                    className={inputClass('vehicleMake')}
                  />
                  {touched.vehicleMake && fieldErrors.vehicleMake && (
                    <p className="mt-1.5 font-dm-sans text-[12px] text-red-400">{fieldErrors.vehicleMake}</p>
                  )}
                </div>
                {/* Warning Light / Issue */}
                <div>
                  <label className="block font-dm-sans text-sm font-semibold text-white mb-2">
                    Warning light or issue <span className="text-[#CC1A1A]">*</span>
                  </label>
                  <select
                    required
                    value={formData.issue}
                    onChange={e => { handleFieldChange('issue', e.target.value); setFormData(p => ({ ...p, issue: e.target.value, otherIssue: '' })); }}
                    onBlur={e => handleFieldBlur('issue', e.target.value)}
                    className={`${inputClass('issue')} appearance-none`}
                    style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='rgba(255,255,255,0.4)' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center' }}
                  >
                    <option value="" disabled>Select an issue...</option>
                    <option value="Airbag Warning Light On">Airbag Warning Light On</option>
                    <option value="SRS Light On">SRS Light On</option>
                    <option value="Seatbelt Not Locking">Seatbelt Not Locking</option>
                    <option value="Airbag Deployed">Airbag Deployed</option>
                    <option value="Multiple Warning Lights">Multiple Warning Lights</option>
                    <option value="Other">Other</option>
                  </select>
                  {touched.issue && fieldErrors.issue && (
                    <p className="mt-1.5 font-dm-sans text-[12px] text-red-400">{fieldErrors.issue}</p>
                  )}
                  {formData.issue === 'Other' && (
                    <div className="mt-3">
                      <textarea
                        rows={3}
                        required
                        value={formData.otherIssue}
                        onChange={e => { setFormData(p => ({ ...p, otherIssue: e.target.value })); if (touched.otherIssue) { const err = e.target.value.trim() ? '' : 'Please describe the issue'; setFieldErrors(prev => { const next = { ...prev }; if (err) next.otherIssue = err; else delete next.otherIssue; return next; }); } }}
                        onBlur={e => { setTouched(prev => ({ ...prev, otherIssue: true })); const err = e.target.value.trim() ? '' : 'Please describe the issue'; setFieldErrors(prev => { const next = { ...prev }; if (err) next.otherIssue = err; else delete next.otherIssue; return next; }); }}
                        placeholder="Please describe the issue..."
                        className={inputClass('otherIssue')}
                      />
                      {touched.otherIssue && fieldErrors.otherIssue && (
                        <p className="mt-1.5 font-dm-sans text-[12px] text-red-400">{fieldErrors.otherIssue}</p>
                      )}
                    </div>
                  )}
                </div>

                {formError && (
                  <p className="font-dm-sans text-sm text-red-400 bg-red-900/20 border border-red-500/30 px-4 py-3 rounded-sm">{formError}</p>
                )}
                {rateLimitMsg && (
                  <p className="font-dm-sans text-sm text-yellow-400 bg-yellow-900/20 border border-yellow-500/30 px-4 py-3 rounded-sm">{rateLimitMsg}</p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full flex items-center justify-center gap-3 px-8 py-4 text-white font-dm-sans font-bold text-sm uppercase tracking-widest transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
                  style={{ background: '#CC1A1A', borderRadius: '3px' }}
                >
                  {submitting ? (
                    <>
                      <svg className="animate-spin" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 12a9 9 0 11-6.219-8.56"/>
                      </svg>
                      Sending...
                    </>
                  ) : (
                    <>Book Diagnostic →</>
                  )}
                </button>
              </form>
            )}
          </div>
        </section>

      </main>
      <Footer />
    </>
  );
}
